import { Action, createReducer, on } from '@ngrx/store';

import * as AiChatActions from './ai-chat.actions';
import * as AiChatFeatureState from './ai-chat.state';

const aiChatReducer = createReducer<AiChatFeatureState.AiChatState>(
  AiChatFeatureState.initialAiChatState,

  on(AiChatActions.resetAiChatState, () => ({
    ...AiChatFeatureState.initialAiChatState
  })),

  on(AiChatActions.newChat, (state) => ({
    ...state,
    messages: [],
    currentConversationId: null,
    error: null,
    isLoading: false
  })),

  on(AiChatActions.getConversationHistory, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AiChatActions.getConversationHistorySuccess, (state, { messages }) => ({
    ...state,
    isLoading: false,
    messages
  })),

  on(AiChatActions.getConversationHistoryFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  on(AiChatActions.loadConversations, (state, { offset }) => ({
    ...state,
    conversationsLoading: true,
    conversationsOffset: offset,
    error: null
  })),

  on(
    AiChatActions.loadConversationsSuccess,
    (state, { conversations, conversationsCount }) => ({
      ...state,
      conversationsLoading: false,
      conversationsCount: conversationsCount,
      conversations:
        state.conversationsOffset === 0
          ? conversations
          : [...state.conversations, ...conversations]
    })
  ),

  on(AiChatActions.loadConversationsFailure, (state, { error }) => ({
    ...state,
    conversationsLoading: false,
    error
  })),

  on(AiChatActions.setCurrentConversation, (state, { conversationId }) => ({
    ...state,
    currentConversationId: conversationId
  })),

  on(AiChatActions.clearMessages, (state) => ({
    ...state,
    messages: []
  })),

  on(AiChatActions.setLoading, (state, { isLoading }) => ({
    ...state,
    isLoading
  })),

  on(AiChatActions.deleteConversation, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AiChatActions.deleteConversationSuccess, (state, { conversationId }) => ({
    ...state,
    isLoading: false,
    conversations: state.conversations.filter(
      (conv) => conv.id !== conversationId
    ),
    currentConversationId:
      state.currentConversationId === conversationId
        ? null
        : state.currentConversationId,
    messages:
      state.currentConversationId === conversationId ? [] : state.messages
  })),

  on(AiChatActions.deleteConversationFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Stream actions
  on(AiChatActions.streamStart, (state, { query }) => ({
    ...state,
    isStreaming: false,
    isLoading: true,
    streamingResponse: '',
    error: null,
    messages: [
      ...state.messages,
      {
        id: Date.now(),
        role: 'user',
        content: query,
        createdAt: new Date().toISOString()
      }
    ]
  })),

  on(AiChatActions.streamConnected, (state) => ({
    ...state,
    isStreaming: true
  })),

  on(AiChatActions.streamChunk, (state, { fullResponse }) => {
    return {
      ...state,
      streamingResponse: fullResponse,
      isStreaming: true,
      isLoading: false
    };
  }),

  on(
    AiChatActions.streamComplete,
    (state, { response, messageId, goals, successSaveGoal, courses, certificates }) => {
      return {
        ...state,
        isStreaming: false,
        isLoading: false,
        streamingResponse: '',
        messages: [
          ...state.messages,
          {
            id: messageId || Date.now(),
            role: 'assistant',
            content: response,
            successSaveGoal,
            createdAt: new Date().toISOString(),
            goals,
            courses,
            certificates
          }
        ]
      };
    }
  ),

  /*
   * V-1. `isLoading: false` is the whole of the "spinner forever" bug.
   *
   * Every other terminal case in this reducer clears it --
   * getConversationHistoryFailure, deleteConversationFailure and streamComplete
   * all do. streamFailure was the sole exception, and because streamStart sets
   * isLoading true while only streamChunk and streamComplete clear it, a
   * failure before the first chunk latched it true for the rest of the session.
   *
   * The blast radius went well past the chat panel: ask-via.component gates
   * every suggestion chip on isLoading, so one failed turn silently killed the
   * Ask Via chips on the occupation and institution pages too, and
   * conversation.component's onSubmit early-returns while isLoading, so the
   * form stayed dead even once the control was re-enabled.
   */
  on(AiChatActions.streamFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    isStreaming: false,
    streamingResponse: '',
    error
  })),

  // Rate limit actions
  on(AiChatActions.getRateLimit, (state) => ({
    ...state,
    error: null
  })),

  on(AiChatActions.getRateLimitSuccess, (state, rateLimit) => ({
    ...state,
    rateLimit
  })),

  on(AiChatActions.getRateLimitFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(AiChatActions.updateTimeUntilReset, (state, { timeUntilReset }) => ({
    ...state,
    rateLimit: state.rateLimit ? { ...state.rateLimit, timeUntilReset } : null
  })),

  // Open chat via actions
  on(AiChatActions.openChatVia, (state) => ({ ...state, isOpenChatVia: true })),
  on(AiChatActions.viaSays, (state, { content }) => ({
    ...state,
    messages: [
      ...state.messages,
      { id: Date.now(), role: 'assistant' as const, content, createdAt: new Date().toISOString() }
    ]
  })),
  on(AiChatActions.closeChatVia, (state) => ({
    ...state,
    isOpenChatVia: false
  })),

  // Rate message actions
  on(
    AiChatActions.rateMessageSuccess,
    (state, { messageId, liked, disliked }) => ({
      ...state,
      messages: state.messages.map((message) =>
        message.id === messageId ? { ...message, liked, disliked } : message
      )
    })
  ),

  on(AiChatActions.rateMessageFailure, (state, { error }) => ({
    ...state,
    error
  }))
);

export function reducer(
  state: AiChatFeatureState.AiChatState | undefined,
  action: Action
) {
  return aiChatReducer(state, action);
}
